import React, { useMemo, useState } from 'react';
import { Card, Tag, Typography } from 'antd';
import type {
  PlayerFeatureAnalysis as PlayerFeatureAnalysisData,
  PlayerPerformanceProfile,
  PlayerRosterAnalysis as PlayerRosterAnalysisData,
} from '../types/analyticsExplanation';
import './PlayerRosterAnalysis.css';

const { Title, Paragraph, Text } = Typography;

const percent = (value: number) => `${(value * 100).toFixed(1)}%`;

const ComparisonBar: React.FC<{
  label: string;
  subject: number;
  playoff: number;
  subjectLabel: string;
  format?: (value: number) => string;
}> = ({ label, subject, playoff, subjectLabel, format = value => value.toFixed(1) }) => {
  const max = Math.max(subject, playoff, 0.1);

  return (
    <div className="player-comparison">
      <div className="player-comparison-label">{label}</div>
      <div className="player-comparison-row">
        <span>{subjectLabel}</span>
        <div><i className="is-subject" style={{ width: `${(subject / max) * 100}%` }} /></div>
        <strong>{format(subject)}</strong>
      </div>
      <div className="player-comparison-row">
        <span>Other playoff avg</span>
        <div><i className="is-playoff" style={{ width: `${(playoff / max) * 100}%` }} /></div>
        <strong>{format(playoff)}</strong>
      </div>
    </div>
  );
};

const RangePlot: React.FC<{ players: PlayerPerformanceProfile[] }> = ({ players }) => {
  const max = Math.max(...players.map(player => player.ceilingPoints), 1);

  return (
    <div className="player-range-plot" role="img" aria-label="Player weekly floor and ceiling ranges">
      <div className="player-range-axis">
        <span>0</span><span>{(max / 2).toFixed(0)}</span><span>{max.toFixed(0)} pts</span>
      </div>
      {players.map(player => {
        const floor = Math.max(0, player.floorPoints);
        const ceiling = Math.max(floor, player.ceilingPoints);
        const ppg = Math.max(0, player.pointsPerGame);
        return (
          <div className="player-range-row" key={`${player.year}-${player.playerId}`}>
            <div className="player-range-name">
              <span>{player.playerName}</span>
              <small>{player.position}{player.positionRankPpg ? ` · PPG rank #${player.positionRankPpg}` : ''}{player.isKeeper ? ' · Keeper' : ''}</small>
            </div>
            <div
              className="player-range-track"
              title={`${player.playerName}: floor ${floor.toFixed(1)}, PPG ${ppg.toFixed(1)}, ceiling ${ceiling.toFixed(1)}, volatility ${player.volatility.toFixed(1)}`}
            >
              <i
                className="player-range-band"
                style={{
                  left: `${(floor / max) * 100}%`,
                  width: `${Math.max(1, ((ceiling - floor) / max) * 100)}%`,
                }}
              />
              <i className="player-range-mean" style={{ left: `${Math.min(100, (ppg / max) * 100)}%` }} />
            </div>
            <div className="player-range-values">
              <span>{floor.toFixed(1)}</span>
              <strong>{ppg.toFixed(1)}</strong>
              <span>{ceiling.toFixed(1)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
const FeatureModelPanel: React.FC<{ analysis: PlayerFeatureAnalysisData }> = ({ analysis }) => {
  const scopes = Array.from(new Set(analysis.featureImportance.map(feature => feature.scope)));

  return (
    <Card className="player-feature-card" bordered={false}>
      <div className="player-feature-heading">
        <div>
          <Title level={3}>What predicts player production?</Title>
          <Paragraph>
            League-wide, season-held-out models estimate roster-week PPG from career context and,
            where dated rankings exist, draft-market context. Bar length is mean absolute SHAP impact.
          </Paragraph>
        </div>
        <Tag color="purple">{analysis.trainingStartYear}-{analysis.trainingEndYear}</Tag>
      </div>
      <div className="player-model-grid">
        <div>
          <span>All qualified players</span>
          <strong>{analysis.careerObservationCount.toLocaleString()} observations</strong>
          <small>
            MAE {analysis.careerContextModel.meanAbsoluteError.toFixed(2)} PPG / R2 {analysis.careerContextModel.r2.toFixed(2)}
          </small>
        </div>
        <div>
          <span>Drafted + pre-draft rank</span>
          <strong>{analysis.rankedDraftObservationCount.toLocaleString()} observations</strong>
          <small>
            MAE {analysis.draftMarketModel.meanAbsoluteError.toFixed(2)} PPG / R2 {analysis.draftMarketModel.r2.toFixed(2)}
          </small>
        </div>
        <div>
          <span>Stored-data coverage</span>
          <strong>{analysis.birthDateCoveragePercentage.toFixed(1)}% age / {analysis.experienceCoveragePercentage.toFixed(1)}% experience</strong>
          <small>{analysis.preDraftRankCoveragePercentage.toFixed(1)}% have eligible dated pre-draft rank context</small>
        </div>
      </div>
      <div className="player-importance-grid">
        {scopes.map(scope => {
          const features = analysis.featureImportance
            .filter(feature => feature.scope === scope)
            .filter(feature => !/-position-(qb|rb|wr)$/.test(feature.key))
            .sort((a, b) => b.meanAbsoluteImpactPointsPerGame - a.meanAbsoluteImpactPointsPerGame)
            .slice(0, 7);
          const max = Math.max(...features.map(feature => feature.meanAbsoluteImpactPointsPerGame), 0.01);
          return (
            <div className="player-importance-group" key={scope}>
              <strong>{scope}</strong>
              {features.map(feature => (
                <div className="player-importance-row" key={feature.key}>
                  <span>{feature.label}</span>
                  <div><i style={{ width: `${(feature.meanAbsoluteImpactPointsPerGame / max) * 100}%` }} /></div>
                  <small>{feature.meanAbsoluteImpactPointsPerGame.toFixed(2)}</small>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      <Text type="secondary">
        Position baselines remain in the models but are omitted above to focus this chart on player context.
        These are retrospective associations, not causal player projections; pre-draft context begins in 2022.
      </Text>
    </Card>
  );
};

const PlayerContextGrid: React.FC<{ players: PlayerPerformanceProfile[] }> = ({ players }) => {
  const contextualized = players.filter(player => player.featureContext);
  if (!contextualized.length) return null;

  return (
    <div className="player-context-grid">
      {contextualized.map(player => {
        const context = player.featureContext!;
        const details = [
          context.ageAtSeason != null ? `Age ${context.ageAtSeason}` : null,
          context.yearsExperience != null ? `${context.yearsExperience} yrs experience` : null,
          context.preDraftPositionRank != null ? `Pre-draft ${player.position} #${context.preDraftPositionRank}` : null,
          context.draftRound != null ? `Round ${context.draftRound}` : null,
        ].filter(Boolean).join(' / ');
        return (
          <div className="player-context-item" key={`${player.year}-${player.playerId}`}>
            <div>
              <strong>{player.playerName}</strong>
              <Tag>{context.acquisitionType}</Tag>
            </div>
            <small>{details}</small>
            <div className="player-driver-list">
              {context.featureDrivers.slice(0, 3).map(driver => (
                <span className={`is-${driver.direction}`} title={driver.detail} key={driver.key}>
                  {driver.label} {driver.impactPointsPerGame >= 0 ? '+' : ''}{driver.impactPointsPerGame.toFixed(2)} PPG
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PlayerRosterAnalysis: React.FC<{
  analysis: PlayerRosterAnalysisData;
  subjectName: string;
}> = ({ analysis, subjectName }) => {
  const years = analysis.seasons.map(season => season.year).sort((a, b) => b - a);
  const [selectedYear, setSelectedYear] = useState(years[0]);
  const selectedSeason = analysis.seasons.find(season => season.year === selectedYear);
  const selectedPlayers = useMemo(
    () => analysis.players.filter(player => player.year === selectedYear),
    [analysis.players, selectedYear],
  );

  if (!selectedSeason) return null;

  return (
    <section className="analytics-section player-analysis-section">
      <div className="analytics-section-heading">
        <div>
          <Title level={2}>Roster talent and player profiles</Title>
          <Paragraph>
            This compares the individuals on {subjectName}’ roster with the other teams that reached the playoffs,
            then separates stable weekly floors, upside ceilings, and scoring composition.
          </Paragraph>
        </div>
        <Tag color="cyan">{analysis.confidence}</Tag>
      </div>

      <div className="player-season-selector">
        <Text strong>Season</Text>
        <select
          aria-label="Player analysis season"
          value={selectedYear}
          onChange={event => setSelectedYear(Number(event.target.value))}
        >
          {years.map(year => <option value={year} key={year}>{year}</option>)}
        </select>
      </div>

      <div className="player-comparison-grid">
        <Card className="player-season-card" bordered={false}>
          <div className="player-season-card-heading">
            <div>
              <span>{selectedYear} roster quality</span>
              <strong>{selectedSeason.qb1Name}</strong>
              <small>
                QB1 · {selectedSeason.qb1Ppg.toFixed(1)} PPG
                {selectedSeason.qb1PositionRank ? ` · rank #${selectedSeason.qb1PositionRank}` : ''}
              </small>
            </div>
            <Tag color={selectedSeason.madePlayoffs ? 'green' : 'volcano'}>
              {selectedSeason.madePlayoffs ? 'Playoffs' : 'Missed playoffs'}
            </Tag>
          </div>
          <ComparisonBar
            label="Elite player count"
            subjectLabel={subjectName}
            subject={selectedSeason.elitePlayerCount}
            playoff={selectedSeason.playoffTeamAverageElitePlayerCount}
          />
          <ComparisonBar
            label="Top-three player PPG"
            subject={selectedSeason.topThreePlayerPpg}
            subjectLabel={subjectName}
            playoff={selectedSeason.playoffTeamAverageTopThreePpg}
          />
          <ComparisonBar
            label="QB1 PPG"
            subject={selectedSeason.qb1Ppg}
            playoff={selectedSeason.playoffTeamAverageQb1Ppg}
            subjectLabel={subjectName}
          />
          <ComparisonBar
            label="Elite keeper count"
            subject={selectedSeason.eliteKeeperCount}
            playoff={selectedSeason.playoffTeamAverageEliteKeeperCount}
            subjectLabel={subjectName}
          />
          <ComparisonBar
            label="Keeper top-three PPG"
            subject={selectedSeason.keeperTopThreePpg}
            playoff={selectedSeason.playoffTeamAverageKeeperTopThreePpg}
            subjectLabel={subjectName}
          />

        </Card>
        <Card className="player-composition-card" bordered={false}>
          <Title level={3}>Touchdowns versus yardage</Title>
          <Paragraph>
            Share of passing, rushing, and receiving points contributed by recorded starters.
          </Paragraph>
          <div className="player-composition-label">
            <span>{subjectName} starters</span>
            <strong>{percent(selectedSeason.touchdownPointShare)} TD points</strong>
          </div>
          <div className="player-composition-stack" aria-label={`${subjectName}: ${percent(selectedSeason.yardagePointShare)} yardage, ${percent(selectedSeason.touchdownPointShare)} touchdowns`}>
            <i className="is-yards" style={{ width: `${selectedSeason.yardagePointShare * 100}%` }} />
            <i className="is-touchdowns" style={{ width: `${selectedSeason.touchdownPointShare * 100}%` }} />
          </div>
          <div className="player-composition-label">
            <span>Playoff-team average</span>
            <strong>{percent(selectedSeason.playoffTeamAverageTouchdownPointShare)} TD points</strong>
          </div>
          <div className="player-composition-stack">
            <i className="is-yards" style={{ width: `${(1 - selectedSeason.playoffTeamAverageTouchdownPointShare) * 100}%` }} />
            <i className="is-touchdowns" style={{ width: `${selectedSeason.playoffTeamAverageTouchdownPointShare * 100}%` }} />
          </div>
          <div className="player-composition-legend">
            <span><i className="is-yards" /> Yardage points</span>
            <span><i className="is-touchdowns" /> Touchdown points</span>
          </div>
        </Card>
      </div>

      {analysis.playerFeatureAnalysis && <FeatureModelPanel analysis={analysis.playerFeatureAnalysis} />}

      <Card className="player-range-card" bordered={false}>
        <div className="player-range-heading">
          <div>
            <Title level={3}>Individual floor and ceiling</Title>
            <Paragraph>
              Roster-week 25th percentile → season PPG marker → 90th percentile.
              Zero weeks remain, so the floor includes availability risk.
            </Paragraph>
          </div>
          <Tag>{selectedPlayers.length} leading players</Tag>
        </div>
        <RangePlot players={selectedPlayers} />
        <PlayerContextGrid players={selectedPlayers} />
      </Card>
    </section>
  );
};

export default PlayerRosterAnalysis;
